import { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Camera,
  Users,
  Settings,
  Menu,
  X,
  LogOut,
  Download,
  UserCircle,
  Grid3X3,
  Package,
  Tv,
  Film,
  Building2,
  ChevronDown,
  Plus,
  Sun,
  Moon,
  CreditCard,
  ShieldCheck,
} from 'lucide-react';
import { useAuthStore } from '../../services/auth';
import { organizationsApi } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';

const baseNavigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Cameras', href: '/cameras', icon: Camera },
  { name: 'Branches', href: '/branches', icon: Building2 },
];

const facesNavItem = { name: 'Faces', href: '/faces', icon: UserCircle };
const zonesNavItem = { name: 'Zones', href: '/zones', icon: Grid3X3 };
const productsNavItem = { name: 'Products', href: '/products', icon: Package };
const mediaPlayersNavItem = { name: 'Media Players', href: '/media-players', icon: Tv };
const videosNavItem = { name: 'Videos', href: '/videos', icon: Film };

const billingNavItem = { name: 'Billing', href: '/billing', icon: CreditCard };
const adminNavItem = { name: 'Admin', href: '/admin', icon: ShieldCheck };

const endNavigation = [
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Downloads', href: '/downloads', icon: Download },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout, organizationId, setOrganization } = useAuthStore();
  const { theme, toggleTheme } = useTheme();

  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch organization to check if face recognition is enabled
  const { data: organization } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => organizationsApi.get(organizationId!),
    enabled: !!organizationId,
  });

  // Super admin: fetch all organizations for switcher
  const { data: allOrgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: organizationsApi.list,
    enabled: isSuperAdmin,
  });

  const createOrgMutation = useMutation({
    mutationFn: (name: string) => organizationsApi.create(name),
    onSuccess: (org) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setOrganization(org.id);
      setShowCreateOrg(false);
      setNewOrgName('');
    },
  });

  const handleSwitchOrg = (orgId: string) => {
    setOrganization(orgId);
    setOrgDropdownOpen(false);
    // Invalidate all queries so they refetch with new org header
    queryClient.invalidateQueries();
  };

  const isStevenAdmin = user?.email === 'steven@mnsa.co.za';

  // Build navigation array based on organization settings
  const navigation = useMemo(() => {
    const items = [...baseNavigation];
    // Face recognition only available to super_admin
    if (isSuperAdmin && organization?.face_recognition_enabled) {
      items.push(facesNavItem);
    }
    if (organization?.zone_monitoring_enabled) {
      // Zones and Products are POC features — only visible to steven@mnsa.co.za
      if (isStevenAdmin) {
        items.push(zonesNavItem);
        items.push(productsNavItem);
      }
      items.push(videosNavItem);
      items.push(mediaPlayersNavItem);
    }
    items.push(billingNavItem);
    if (isSuperAdmin) {
      items.push(adminNavItem);
    }
    items.push(...endNavigation);
    return items;
  }, [isSuperAdmin, isStevenAdmin, organization?.face_recognition_enabled, organization?.zone_monitoring_enabled]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 transition-colors">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-900 shadow-xl dark:shadow-black/30 transform transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b dark:border-slate-800">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="HumanVu" className="h-10 w-auto" />
            <span className="text-xl font-bold text-gray-900 dark:text-white">HumanVu</span>
          </div>
          <button onClick={() => setSidebarOpen(false)}>
            <X className="w-6 h-6 text-gray-500 dark:text-slate-400" />
          </button>
        </div>
        <nav className="mt-4 px-2 space-y-0.5">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white font-medium'
                    : 'text-gray-600 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-800'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={`w-5 h-5 mr-3 ${isActive ? '' : 'opacity-60'}`} />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-800/50 transition-colors">
          <div className="flex items-center h-16 px-6 border-b dark:border-slate-800">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="HumanVu" className="h-10 w-auto" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">HumanVu</span>
            </div>
          </div>
          <nav className="flex-1 mt-4 px-3 space-y-0.5">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white font-medium'
                      : 'text-gray-600 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <item.icon className={`w-5 h-5 mr-3 ${isActive ? '' : 'opacity-60'}`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          {/* Super admin org switcher */}
          {isSuperAdmin && (
            <div className="px-3 py-2 border-t dark:border-slate-800">
              <div className="relative">
                <button
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm bg-gray-50 dark:bg-slate-800 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="w-4 h-4 text-gray-500 dark:text-slate-400 flex-shrink-0" />
                    <span className="truncate text-gray-700 dark:text-slate-300">
                      {organization?.name || 'Select org'}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0 transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {orgDropdownOpen && (
                  <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-lg dark:shadow-black/30 max-h-64 overflow-y-auto z-50">
                    {allOrgs?.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => handleSwitchOrg(org.id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors ${
                          org.id === organizationId ? 'bg-primary-500/10 text-primary-500 dark:text-primary-400 font-medium' : 'text-gray-700 dark:text-slate-300'
                        }`}
                      >
                        {org.name}
                      </button>
                    ))}
                    <button
                      onClick={() => { setShowCreateOrg(true); setOrgDropdownOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-slate-700 border-t dark:border-slate-700 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      New Organization
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="p-4 border-t dark:border-slate-800">
            <div className="flex items-center">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.email}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 dark:text-slate-600 hover:text-gray-600 dark:hover:text-slate-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex items-center h-16 px-4 bg-slate-900/80 border-b border-slate-800 backdrop-blur-md lg:px-8 transition-colors">
          <button
            className="p-2 text-slate-400 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center space-x-4">
            <span className="text-xs text-slate-500">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-400 hover:bg-slate-800 transition-colors"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 lg:p-8">{children}</main>
      </div>

      {/* Create Organization Modal */}
      {showCreateOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl dark:shadow-black/30 p-6 w-full max-w-md mx-4 border dark:border-slate-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create Organization</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newOrgName.trim()) {
                  createOrgMutation.mutate(newOrgName.trim());
                }
              }}
            >
              <input
                type="text"
                placeholder="Organization name"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                className="w-full px-3 py-2 border dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
                autoFocus
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowCreateOrg(false); setNewOrgName(''); }}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newOrgName.trim() || createOrgMutation.isPending}
                  className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-500 disabled:opacity-50 transition-colors"
                >
                  {createOrgMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
